import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { signOut, useSessionAuth } from "@/app/_utils/sessionClient";
import { cn } from "@/app/_utils/cn";
const Logo = "/cuologo.png";

import { navOptions } from "./NavigationOptions";

import NavigationCollapseable from "./NavigationCollapseable";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/admin/_common/ui/tooltip";
import {
  LogOut,
  Settings,
  ExternalLink,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronDown,
} from "lucide-react";

const COLLAPSED_KEY = "cuo_sidebar_collapsed";
// Supersedes the old cuo_sidebar_collapsed_sections list. A new key rather than a
// migration: only one section can be open now, so a stored list of closed ones has no
// meaningful translation, and the default is a reasonable place to land.
const OPEN_SECTION_KEY = "cuo_sidebar_open_section";

type NavGroup = { section: string | null; items: any[] };

/**
 * Turn the flat navOptions list into groups.
 *
 * `section: true` entries are headers rather than containers — the list is flat — so
 * each header owns everything after it until the next one. Items appearing before any
 * header (Home, Shows, …) land in a leading group with no header, which is never
 * collapsible because there would be nothing to click.
 */
function toGroups(options: any[]): NavGroup[] {
  const groups: NavGroup[] = [{ section: null, items: [] }];
  options.forEach((option) => {
    if (option.section) {
      groups.push({ section: option.label, items: [] });
    } else {
      groups[groups.length - 1].items.push(option);
    }
  });
  return groups.filter((group) => group.items.length > 0);
}

/**
 * Whether a nav item points at the current path.
 *
 * Mirrors NavigationButton's own `selectedIndex.match(...)` test, including its
 * substring behaviour, so a section can never be judged inactive while the item inside
 * it is rendering as selected. Also checks children, since a parent item's own link may
 * not be the page you are on.
 */
function isActiveItem(item: any, path: string): boolean {
  if (item.link && path.match(`/admin${item.link}`)) return true;
  return Boolean(item.children?.some((child: any) => isActiveItem(child, path)));
}

/** The one open section, or null for all closed — which is the default. */
function readOpenSection(): string | null {
  try {
    return localStorage.getItem(OPEN_SECTION_KEY) || null;
  } catch {
    return null;
  }
}

export default function Navigation() {
  return (
    <div className="hidden h-full sidebar:flex">
      <NavigationContent />
    </div>
  );
}

export function NavigationContent({
  className,
  onRequestClose,
}: { className?: string; onRequestClose?: () => void } = {}) {
  const navigate = useNavigate();
  const { session, can } = useSessionAuth();
  const { pathname: path } = useLocation();
  const [selectedIndex, setSelectedIndex] = useState(path);
  const [collapsed, setCollapsed] = useState(
    () => !onRequestClose && localStorage.getItem(COLLAPSED_KEY) === "1"
  );
  // One open section at a time, so this is a single value rather than a set. That
  // choice is what removes the "no preference yet" special case the list version
  // needed: null simply means nothing is open, which is also the default.
  const [openSection, setOpenSection] = useState<string | null>(readOpenSection);

  useEffect(() => {
    setSelectedIndex(path);
  }, []);

  useEffect(() => {
    setSelectedIndex(path);
  }, [path]);

  useEffect(() => {
    localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  const groups = React.useMemo(() => {
    if (!session) return [];
    return toGroups(navOptions.filter((option: any) => can(option.permission)));
  }, [session, can]);

  useEffect(() => {
    // The section holding the page you are on opens on load, so a fresh render never
    // hides where you are. It wins over whatever was stored -- with only one section
    // open at a time, the alternative is showing an open section you are not in while
    // the one you are in stays shut. Not written to storage, so navigating away later
    // still restores the section you actually chose.
    const active = groups.find((group) =>
      group.items.some((item: any) => isActiveItem(item, path))
    );
    if (active?.section) setOpenSection(active.section);
  }, [groups]);

  function toggleSection(section: string) {
    // Opening one closes the rest; clicking the open one closes it.
    const next = openSection === section ? null : section;
    setOpenSection(next);
    try {
      localStorage.setItem(OPEN_SECTION_KEY, next ?? "");
    } catch {
      // A full or blocked localStorage should not stop the section toggling.
    }
  }

  const username: string | undefined =
    session?.user?.first_name || session?.user?.email || session?.user?.username;
  // The footer shows one role; a user with several sees the first alphabetically.
  const roleName: string | undefined = session?.roles?.[0];
  const initials = username ? username.slice(0, 2).toUpperCase() : "??";

  return (
    <TooltipProvider delayDuration={200}>
    <div
      className={cn(
        "flex flex-none flex-col overflow-hidden border-r border-border bg-[hsl(var(--page-background))] transition-[width] duration-150",
        collapsed ? "w-14 px-1.5 py-3" : "w-[248px] p-3",
        className
      )}
    >
      {collapsed && !onRequestClose && (
        <button
          type="button"
          title="Expand sidebar"
          onClick={() => setCollapsed(false)}
          className="mb-1 flex size-7 flex-none items-center justify-center self-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <PanelLeftOpen className="size-4 shrink-0" size={16} />
        </button>
      )}

      <div
        className={cn(
          "flex items-center gap-1",
          collapsed ? "mb-1 pb-1 pt-0" : "mb-3 pb-3 pt-1"
        )}
      >
        <a
          href="/admin/home"
          onClick={(e) => {
            e.preventDefault();
            navigate("/admin/home");
          }}
          className={cn(
            "flex min-w-0 flex-1 items-center gap-2.5 rounded-md px-1",
            collapsed && "justify-center"
          )}
        >
          <img
            src={Logo}
            alt="comedyuo-logo"
            className={cn(
              "h-8 flex-none object-contain object-left",
              collapsed ? "w-8" : "w-40"
            )}
          />
        </a>
        {!collapsed && !onRequestClose && (
          <button
            type="button"
            title="Collapse sidebar"
            onClick={() => setCollapsed(true)}
            className="flex size-7 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <PanelLeftClose className="size-4 shrink-0" size={16} />
          </button>
        )}
        {onRequestClose && (
          <button
            type="button"
            title="Close navigation"
            onClick={onRequestClose}
            className="flex size-7 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <PanelLeftClose className="size-4 shrink-0" size={16} />
          </button>
        )}
      </div>

      <nav className="-mx-1 flex min-h-0 flex-1 flex-col gap-px overflow-y-auto overflow-x-hidden px-1">
        {session &&
          groups.map((group, groupIndex) => {
            const isSectionCollapsed =
              group.section !== null && group.section !== openSection;
            // An icon-only sidebar has no header to click, so its sections always show
            // -- otherwise those items would be unreachable rather than merely hidden.
            const showItems = collapsed || !isSectionCollapsed;

            const renderItem = (menuItem: any, index: number) => {
              if (menuItem.children) {
                return collapsed ? (
                  <NavigationButton
                    key={index}
                    menuItem={menuItem}
                    selectedIndex={selectedIndex}
                    collapsed
                    onClick={() => {
                      navigate(`/admin${menuItem.link}`);
                      onRequestClose?.();
                    }}
                  />
                ) : (
                  <NavigationCollapseable
                    key={index}
                    menuItem={menuItem}
                    selectedIndex={selectedIndex}
                    onNavigate={onRequestClose}
                  />
                );
              }
              return (
                <NavigationButton
                  key={index}
                  menuItem={menuItem}
                  selectedIndex={selectedIndex}
                  collapsed={collapsed}
                  onClick={() => {
                    if (menuItem.link.includes("http")) {
                      window.open(menuItem.link, "_blank");
                    } else {
                      navigate(`/admin${menuItem.link}`);
                    }
                    onRequestClose?.();
                  }}
                />
              );
            };

            return (
              <React.Fragment key={groupIndex}>
                {group.section !== null &&
                  // With the sidebar itself collapsed there is no header to click, so
                  // sections stay dividers and every item remains reachable by icon.
                  (collapsed ? (
                    <div className="my-2 h-px bg-border" />
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggleSection(group.section!)}
                      aria-expanded={!isSectionCollapsed}
                      className="mb-1 mt-3.5 flex items-center gap-1 rounded-md py-0.5 pl-2.5 pr-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70 hover:text-muted-foreground"
                    >
                      <span className="flex-1 text-left">{group.section}</span>
                      <ChevronDown
                        className={cn(
                          "size-3.5 flex-none transition-transform duration-200 ease-out",
                          isSectionCollapsed && "-rotate-90"
                        )}
                      />
                    </button>
                  ))}
                {/*
                  Animated with grid-template-rows 0fr -> 1fr rather than a height
                  transition, because the section's height is not known up front and
                  transitioning to `auto` does not animate. The config's accordion
                  keyframes are no help here either -- they interpolate to
                  --radix-accordion-content-height, which only a Radix primitive sets.

                  The inner div needs overflow-hidden for two reasons: it clips the
                  items mid-collapse, and it is what lets a grid item shrink below its
                  content's automatic minimum size.

                  Visibility is transitioned alongside it so the items leave the tab
                  order once closed. It changes discretely -- at the end of the
                  duration when hiding, immediately when showing -- which is exactly
                  the timing wanted, and is why this is not just `hidden`.
                */}
                <div
                  className={cn(
                    "grid transition-[grid-template-rows] duration-200 ease-out",
                    showItems ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  )}
                >
                  <div
                    className={cn(
                      "flex flex-col gap-px overflow-hidden transition-[visibility] duration-200",
                      showItems ? "visible" : "invisible"
                    )}
                  >
                    {group.items.map(renderItem)}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
      </nav>

      <div className="flex flex-col gap-px border-t border-border pt-1.5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                "mt-1 flex items-center gap-2.5 rounded-md p-2 hover:bg-muted",
                collapsed && "justify-center"
              )}
              title={collapsed ? `${username ?? "Unknown user"} · ${roleName ?? ""}` : undefined}
            >
              <div className="flex size-[30px] flex-none items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                {initials}
              </div>
              {!collapsed && (
                <div className="flex min-w-0 flex-1 flex-col text-left leading-tight">
                  <span className="truncate text-[13px] font-semibold text-foreground">
                    {username ?? "Unknown user"}
                  </span>
                  <span className="truncate text-[11.5px] capitalize text-muted-foreground">
                    {roleName ?? ""}
                  </span>
                </div>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top" className="w-48">
            <DropdownMenuItem onClick={() => navigate("/admin/settings")}>
              <Settings className="mr-2 size-4" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => signOut()}
              className="text-red-600 focus:bg-red-50 focus:text-red-700"
            >
              <LogOut className="mr-2 size-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {!collapsed && (
          <p className="mt-1 px-2 text-[10px] text-muted-foreground/70">
            Comedy Underground Overground, LLC. © 2023. All rights reserved.
          </p>
        )}
      </div>
    </div>
    </TooltipProvider>
  );
}

const NavigationButton = ({
  menuItem,
  selectedIndex,
  onClick,
  collapsed,
}: {
  menuItem: any;
  selectedIndex?: any;
  onClick: Function;
  collapsed?: boolean;
}) => {
  const match = selectedIndex?.match(`/admin${menuItem.link}`);
  const { link } = menuItem;
  const Icon = match ? menuItem.selectedIcon : menuItem.icon;

  const button = (
    <button
      type="button"
      onClick={() => onClick()}
      className={cn(
        "flex items-center gap-2.5 rounded-md text-[13.5px] font-medium",
        collapsed ? "size-9 flex-none justify-center self-center p-0" : "px-2.5 py-2",
        match
          ? "bg-muted text-foreground"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      )}
    >
      <Icon className="size-4 flex-none" />
      {!collapsed && (
        <>
          <span className="flex-1 truncate text-left">{menuItem.label}</span>
          {link?.includes("http") && (
            <ExternalLink className="size-3.5 flex-none text-muted-foreground" />
          )}
        </>
      )}
    </button>
  );

  if (!collapsed) return button;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right">{menuItem.label}</TooltipContent>
    </Tooltip>
  );
};
